import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Leaf, Loader2 } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email.trim().toLowerCase(), password);
      navigate("/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2" style={{ backgroundColor: "#FAF9F6" }}>
      <div className="hidden lg:flex flex-col justify-between p-12" style={{ backgroundColor: "#647D6C" }}>
        <div className="flex items-center gap-2 text-white">
          <span className="grid place-items-center w-9 h-9 rounded-md bg-white/15">
            <Leaf className="w-5 h-5" strokeWidth={1.75} />
          </span>
          <span className="font-heading font-semibold text-xl">Verdant HR</span>
        </div>
        <div className="text-white max-w-md">
          <h1 className="font-heading text-4xl font-medium tracking-tight leading-tight">
            Time off, made calm and clear.
          </h1>
          <p className="mt-4 text-white/80 leading-relaxed">
            Request holidays, log sick days, and see who's off — all in one simple place for your team.
          </p>
        </div>
        <p className="text-white/60 text-sm">Built for small teams of 1–10 people.</p>
      </div>

      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <span className="grid place-items-center w-9 h-9 rounded-md" style={{ backgroundColor: "#647D6C" }}>
              <Leaf className="w-5 h-5 text-white" strokeWidth={1.75} />
            </span>
            <span className="font-heading font-semibold text-xl">Verdant HR</span>
          </div>
          <h2 className="font-heading text-2xl font-medium tracking-tight">Welcome back</h2>
          <p className="text-sm mt-1" style={{ color: "#6A736D" }}>
            Sign in to manage your leave.
          </p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <label className="text-sm font-medium" style={{ color: "#2C332E" }}>Email</label>
              <input
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="mt-1.5 w-full bg-white border rounded-md px-3 py-2 outline-none focus:ring-2 focus:ring-[#647D6C]/20 focus:border-[#647D6C]"
                style={{ borderColor: "#EAE8E1" }}
                placeholder="you@company.co"
              />
            </div>
            <div>
              <label className="text-sm font-medium" style={{ color: "#2C332E" }}>Password</label>
              <input
                data-testid="login-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="mt-1.5 w-full bg-white border rounded-md px-3 py-2 outline-none focus:ring-2 focus:ring-[#647D6C]/20 focus:border-[#647D6C]"
                style={{ borderColor: "#EAE8E1" }}
                placeholder="••••••••"
              />
            </div>
            {error && (
              <p data-testid="login-error" className="text-sm" style={{ color: "#8C3B4A" }}>
                {error}
              </p>
            )}
            <button
              data-testid="login-submit-button"
              type="submit"
              disabled={loading}
              className="w-full text-white rounded-md px-4 py-2.5 font-medium transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
              style={{ backgroundColor: "#647D6C" }}
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              Sign in
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
